import axios from 'axios';
import { API_ENDPOINTS } from '../config/env';

export const PROCESSING_FEE_UNLOCK_HEADER = 'X-Processing-Fee-Unlock';

export type FeeFlowType = 'DirectCrypto' | 'FiatOnramp';

export interface SuperAgentFee {
  userId: number;
  percent: number;
}

export interface AgentFee {
  userId: number;
  percent: number;
}

export interface FlowFeeSettings {
  flowType: FeeFlowType;
  defaultProcessingPercent: number;
  defaultSuperAgentPercent: number;
  defaultAgentPercent: number;
  superAgentPercent: number | null;
  superAgentFees: SuperAgentFee[];
  agentFees: AgentFee[];
}

export interface FeeSettings {
  flowSettings: FlowFeeSettings[];
}

export interface UpdateFeeRequest {
  type: 'Company' | 'SuperAgent' | 'Agent';
  targetId?: number | null;
  percent: number;
  flowType: FeeFlowType;
}

export interface UpdateFeeResponse {
  updated: boolean;
}

export interface ProcessingFeeUnlockResponse {
  unlocked: boolean;
  unlockToken: string;
  expiresAtUtc: string;
}

export interface AddCustomFeeRequest {
  userId: number;
  feeType: 'SuperAgent' | 'Agent';
  percent: number;
  flowType: FeeFlowType;
}

class FeeSettingsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getFeeSettings(): Promise<FeeSettings> {
    const response = await axios.get(API_ENDPOINTS.SETTINGS_FEES, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  async unlockProcessingFeePanel(password: string): Promise<ProcessingFeeUnlockResponse> {
    const response = await axios.post(API_ENDPOINTS.SETTINGS_FEES_PLATFORM_UNLOCK, {
      password,
    }, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  async updateFeeSettings(updateData: UpdateFeeRequest, unlockToken?: string | null): Promise<UpdateFeeResponse> {
    const headers: Record<string, string> = this.getAuthHeaders();
    const endpoint = updateData.type === 'Company'
      ? API_ENDPOINTS.SETTINGS_FEES_PLATFORM_UPDATE
      : API_ENDPOINTS.SETTINGS_FEES;

    if (updateData.type === 'Company' && unlockToken) {
      headers[PROCESSING_FEE_UNLOCK_HEADER] = unlockToken;
    }

    const response = await axios.post(endpoint, updateData, {
      headers,
    });

    return response.data;
  }

  async addCustomFee(addData: AddCustomFeeRequest): Promise<UpdateFeeResponse> {
    const response = await axios.post(API_ENDPOINTS.SETTINGS_FEES, {
      type: addData.feeType,
      targetId: addData.userId,
      percent: addData.percent,
      flowType: addData.flowType
    }, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }
}

export const feeSettingsService = new FeeSettingsService();
